import { useState, useMemo } from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command"

export function ArticleCombobox({
  articles = [],
  value,
  onSelect,
  placeholder = "Select an article...",
  showCategoryFilter = false,
  emptyLabel = "None selected",
}) {
  const [open, setOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)

  const selectedArticle = useMemo(
    () => articles.find((a) => a.id?.toString() === value?.toString()),
    [articles, value]
  )

  const categories = useMemo(() => {
    if (!showCategoryFilter) return []
    const names = [...new Set(articles.map((a) => a.categoryName).filter(Boolean))]
    return names.sort()
  }, [articles, showCategoryFilter])

  const filteredArticles = useMemo(() => {
    if (!selectedCategory) return articles
    return articles.filter((a) => a.categoryName === selectedCategory)
  }, [articles, selectedCategory])

  const grouped = useMemo(() => {
    const groups = {}
    for (const article of filteredArticles) {
      const key = article.authorName || "Unknown"
      if (!groups[key]) groups[key] = []
      groups[key].push(article)
    }
    // Sort groups by author name, titles already sorted by backend
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredArticles])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-10 justify-between bg-white border-zinc-300 text-zinc-900 font-normal hover:bg-zinc-50 px-3"
        >
          {selectedArticle ? (
            <span className="truncate text-sm">
              {selectedArticle.title}
              {selectedArticle.authorName && (
                <span className="text-zinc-400"> — {selectedArticle.authorName}</span>
              )}
            </span>
          ) : (
            <span className="text-zinc-400 text-sm">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          {showCategoryFilter && categories.length > 1 && (
            <div className="flex flex-wrap gap-1 px-3 pt-2 pb-1 border-b border-zinc-100">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium transition-colors",
                  !selectedCategory
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                )}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                  className={cn(
                    "px-2 py-0.5 rounded-full text-xs font-medium transition-colors",
                    selectedCategory === cat
                      ? "bg-zinc-900 text-white"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
          <CommandInput placeholder="Search articles..." className="h-11" />
          <CommandList className="max-h-[280px]">
            <CommandEmpty className="py-8 text-center text-sm text-zinc-500">
              No articles found.
            </CommandEmpty>
            <CommandGroup className="p-1.5">
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onSelect("")
                  setOpen(false)
                }}
                className="px-3 py-2.5 rounded-md"
              >
                <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                <span className="text-zinc-400 italic text-sm">{emptyLabel}</span>
              </CommandItem>
            </CommandGroup>
            {grouped.map(([authorName, authorArticles]) => (
              <CommandGroup
                key={authorName}
                heading={authorName}
                className="p-1.5"
              >
                {authorArticles.map((article) => (
                  <CommandItem
                    key={article.id}
                    value={[article.title, article.authorName, article.intervieweeName, article.categoryName, article.id].filter(Boolean).join(" ")}
                    onSelect={() => {
                      onSelect(article.id?.toString() || article.id)
                      setOpen(false)
                    }}
                    className="px-3 py-2.5 rounded-md"
                  >
                    <Check className={cn("mr-2 h-4 w-4 flex-shrink-0", value?.toString() === article.id?.toString() ? "opacity-100" : "opacity-0")} />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-zinc-900">
                        {article.title}
                      </span>
                      {article.categoryName && (
                        <span className="block truncate text-xs text-zinc-400">
                          {article.categoryName}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
